import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { ConfigProvider, DatePicker, Input, Popover, Select } from 'antd';
import { DownOutlined, FileExcelOutlined, FilePdfOutlined, SearchOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import useAxiosClient from '../axios-client.js';
import { useStateContext } from '../contexts/ContextProvider.jsx';
import {
  FilterChip, LoadingRow, MenuList, PageBtn, PanelDrawer, SummaryStat,
  filterBtn, ghostBtn, miniLabel,
} from '../components/Stock/stockChrome.jsx';
import { PRIMARY, shellAntdTheme, shellVars, STATUS } from '../shellTokens';

const { RangePicker } = DatePicker;

const PAGE_SIZE_OPTIONS = [15, 30, 50];

const PRESETS = [
  { key: 'thisMonth', label: 'This month', range: () => [dayjs().startOf('month'), dayjs().endOf('month')] },
  { key: 'lastMonth', label: 'Last month', range: () => [dayjs().subtract(1, 'month').startOf('month'), dayjs().subtract(1, 'month').endOf('month')] },
  { key: 'last90', label: 'Last 90 days', range: () => [dayjs().subtract(90, 'day'), dayjs()] },
  { key: 'all', label: 'All time', range: () => null },
];

const fmtKg = (n) => Number(n ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtSize = (bytes) => {
  const kb = (Number(bytes) || 0) / 1024;
  return kb >= 1024 ? `${(kb / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(kb))} KB`;
};

const useIsMobile = () => {
  const [mobile, setMobile] = useState(() => window.innerWidth < 768);
  useEffect(() => {
    const onResize = () => setMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return mobile;
};

/** Fluid columns with floors, so the row never forces the table sideways. */
const GRID = [
  'minmax(64px,0.8fr)',  // date
  'minmax(0,1.6fr)',     // driver
  'minmax(0,2.2fr)',     // fabric
  'minmax(76px,1fr)',    // weighed
  'minmax(70px,0.9fr)',  // variance
  'minmax(62px,0.8fr)',  // copies
].join(' ');

const cell = {
  padding: '11px 8px', font: '400 12.5px Outfit, sans-serif',
  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
};
const numCell = { ...cell, textAlign: 'right', font: '600 12.5px monospace' };
const headCell = {
  padding: '0 8px 8px', ...miniLabel,
  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
};

/**
 * Fabric deliveries — goods in, and the challan copies that evidence them.
 *
 * Every receipt was weighed against a supplier's paperwork, and the scans of
 * that paperwork are the reason this page exists: a challan is what a dispute
 * over a short delivery is settled with, so it has to be findable months later
 * by the date or the driver who brought it.
 */
function FabricReceipts() {
  const axiosClient = useAxiosClient();
  const { darkMode } = useStateContext();
  const isMobile = useIsMobile();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);

  const [preset, setPreset] = useState('thisMonth');
  const [rangeMenuOpen, setRangeMenuOpen] = useState(false);
  const [range, setRange] = useState(() => PRESETS[0].range());
  const [driverInput, setDriverInput] = useState('');
  const [driver, setDriver] = useState('');

  const [detail, setDetail] = useState(null);
  const [exporting, setExporting] = useState(false);

  const fetchReceipts = useCallback(async (overrides = {}) => {
    const p = overrides.page ?? page;
    const size = overrides.pageSize ?? pageSize;
    const r = 'range' in overrides ? overrides.range : range;
    const driverTerm = 'driver' in overrides ? overrides.driver : driver;

    setLoading(true);
    try {
      const res = await axiosClient.get('/fabricStock/receipts', {
        params: {
          page: p,
          perPage: size,
          from: r?.[0] ? r[0].format('YYYY-MM-DD') : undefined,
          to: r?.[1] ? r[1].format('YYYY-MM-DD') : undefined,
          driver: driverTerm || undefined,
        },
      });
      setRows(res.data.data || []);
      setTotal(res.data.meta?.total || 0);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Could not load deliveries');
    } finally {
      setLoading(false);
    }
  }, [axiosClient, page, pageSize, range, driver]);

  useEffect(() => { fetchReceipts(); }, [fetchReceipts]);

  /**
   * Re-fetch the one receipt when its drawer opens.
   *
   * The challan links are presigned and expire in an hour, so the copy handed
   * out with the list could already be dead by the time someone opens a row
   * they scrolled past earlier. Asking again costs one request and guarantees
   * the link works when it is clicked.
   */
  useEffect(() => {
    if (!detail?.id) return undefined;

    let live = true;
    axiosClient.get(`/fabricStock/receipts/${detail.id}`)
      .then((res) => { if (live && res.data.data) setDetail(res.data.data); })
      .catch(() => { /* the row's own copy still renders; only the links may be stale */ });

    return () => { live = false; };
    // Keyed on the id alone: setDetail below would otherwise re-trigger this.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [axiosClient, detail?.id]);

  const applyPreset = (key) => {
    if (key === 'custom') {
      setPreset('custom');
      return;
    }
    const found = PRESETS.find((p) => p.key === key);
    const next = found ? found.range() : null;
    setPreset(key);
    setRange(next);
    setPage(1);
    fetchReceipts({ range: next, page: 1 });
  };

  const applyRange = (value) => {
    setPreset('custom');
    setRange(value);
    setPage(1);
    fetchReceipts({ range: value, page: 1 });
  };

  const applyDriver = (value) => {
    setDriver(value);
    setPage(1);
    fetchReceipts({ driver: value, page: 1 });
  };

  /**
   * The whole range as a workbook, not the page on screen.
   *
   * Built on the server: the table holds fifteen rows and the point of the
   * export is everything the filters match, which the client has never seen.
   * It also has to be an .xlsx rather than the CSV the other pages emit, so the
   * per-delivery and per-fabric-line sheets can travel in one file.
   */
  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await axiosClient.get('/fabricStock/receipts/export', {
        params: {
          from: range?.[0] ? range[0].format('YYYY-MM-DD') : undefined,
          to: range?.[1] ? range[1].format('YYYY-MM-DD') : undefined,
          driver: driver || undefined,
        },
        responseType: 'blob',
      });

      const span = range?.[0]
        ? `${range[0].format('YYYY-MM-DD')}-to-${range[1].format('YYYY-MM-DD')}`
        : 'all-time';

      const url = window.URL.createObjectURL(new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `fabric-deliveries-${span}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      // The error body came back as a blob because that is what was asked for,
      // so it has to be read as text before it can say anything useful.
      let message = 'Could not export the deliveries';
      try {
        const parsed = JSON.parse(await error.response?.data?.text());
        message = parsed.message || message;
      } catch { /* not JSON — keep the generic message */ }
      toast.error(message);
    } finally {
      setExporting(false);
    }
  };

  const rangeLabel = useMemo(() => {
    if (preset !== 'custom') return PRESETS.find((p) => p.key === preset)?.label || 'All time';
    if (!range?.[0]) return 'All time';
    return `${range[0].format('D MMM')} – ${range[1].format('D MMM YYYY')}`;
  }, [preset, range]);

  const lastPage = Math.max(1, Math.ceil(total / pageSize));
  const rootStyle = shellVars(darkMode);

  const fabricSummary = (r) => (r.lines || [])
    .map((l) => `${l.fabricType} ${l.fabricColor}`)
    .join(', ') || '—';

  return (
    <div className="et-rec" style={rootStyle}>
      <style>{`
        .et-rec{font-family:Outfit,system-ui,sans-serif;color:var(--text);display:flex;flex-direction:column;gap:14px;height:100%;min-height:0}
        .et-rec__row:hover{background:var(--hover)}
        .et-rec__ghost:hover{background:var(--hover)}
        .et-rec__file{transition:border-color .12s}
        .et-rec__file:hover{border-color:${PRIMARY}}
      `}</style>

      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 24, flexWrap: 'wrap' }}>
        <div>
          <div style={{ font: '700 22px Outfit, sans-serif', letterSpacing: '-.01em' }}>Fabric Deliveries</div>
          <div style={{ font: '400 13px Outfit, sans-serif', color: 'var(--muted)', marginTop: 2 }}>
            Goods in, with their challan copies · {rangeLabel}
          </div>
        </div>
        <button
          type="button"
          className="et-rec__ghost"
          onClick={handleExport}
          disabled={exporting}
          style={{ ...ghostBtn, opacity: exporting ? 0.6 : 1 }}
        >
          <FileExcelOutlined style={{ color: 'var(--muted)', fontSize: 12 }} />
          {exporting ? 'Building…' : `Export Excel · ${rangeLabel}`}
        </button>
      </div>

      <ConfigProvider theme={shellAntdTheme(darkMode)}>
        {/* Filters */}
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          <Popover
            open={rangeMenuOpen}
            onOpenChange={setRangeMenuOpen}
            trigger="click"
            placement="bottomLeft"
            content={
              <MenuList
                darkMode={darkMode}
                active={preset}
                items={[...PRESETS.map((p) => ({ key: p.key, label: p.label })), { key: 'custom', label: 'Custom range' }]}
                onSelect={(key) => { applyPreset(key); setRangeMenuOpen(false); }}
              />
            }
          >
            <button type="button" style={filterBtn}>
              {rangeLabel} <DownOutlined style={{ fontSize: 9, color: 'var(--muted)' }} />
            </button>
          </Popover>

          {preset === 'custom' && (
            <RangePicker value={range} onChange={applyRange} format="D MMM YYYY" style={{ height: 38 }} />
          )}

          <Input
            allowClear
            value={driverInput}
            onChange={(e) => setDriverInput(e.target.value)}
            onPressEnter={() => applyDriver(driverInput.trim())}
            onBlur={() => { if (driverInput.trim() !== driver) applyDriver(driverInput.trim()); }}
            prefix={<SearchOutlined style={{ color: 'var(--dim)' }} />}
            placeholder="Driver who brought it"
            style={{ width: 240, height: 38 }}
          />

          {driver && <FilterChip label="Driver" value={driver} onClear={() => { setDriverInput(''); applyDriver(''); }} />}
        </div>

        {/* Deliveries */}
        <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, padding: '14px 12px 8px', minWidth: 0, overflow: 'auto' }}>
          {!isMobile && (
            <div style={{ display: 'grid', gridTemplateColumns: GRID, borderBottom: '1px solid var(--border)' }}>
              <div style={{ ...headCell, paddingLeft: 12 }}>Date</div>
              <div style={headCell}>Driver</div>
              <div style={headCell}>Fabric</div>
              <div style={{ ...headCell, textAlign: 'right' }}>Weighed</div>
              <div style={{ ...headCell, textAlign: 'right' }}>Variance</div>
              <div style={{ ...headCell, textAlign: 'right' }}>Copies</div>
            </div>
          )}

          {loading && <LoadingRow />}
          {!loading && rows.length === 0 && <EmptyState />}

          {!loading && !isMobile && rows.map((r) => (
            <div
              key={r.id}
              role="button" tabIndex={0}
              className="et-rec__row"
              onClick={() => setDetail(r)}
              onKeyDown={(e) => { if (e.key === 'Enter') setDetail(r); }}
              style={{ display: 'grid', gridTemplateColumns: GRID, borderBottom: '1px solid var(--border)', alignItems: 'center', cursor: 'pointer' }}
            >
              <div style={{ ...cell, paddingLeft: 12, color: 'var(--muted)', fontFamily: 'monospace', fontSize: 12 }}>
                {dayjs(r.receivedOn).format('D MMM')}
              </div>
              <div style={{ ...cell, fontWeight: 600 }}>{r.driver || '—'}</div>
              <div style={{ ...cell, color: 'var(--muted)' }}>{fabricSummary(r)}</div>
              <div style={numCell}>{fmtKg(r.actualWeight)}</div>
              <div style={{ ...numCell, color: varianceTint(r.variance) }}>
                {r.variance === null || r.variance === undefined ? '—' : `${r.variance > 0 ? '+' : ''}${fmtKg(r.variance)}`}
              </div>
              <div style={{ ...numCell, color: (r.files || []).length ? 'var(--text)' : 'var(--dim)' }}>
                {(r.files || []).length || '—'}
              </div>
            </div>
          ))}

          {!loading && isMobile && rows.map((r) => (
            <div
              key={r.id}
              role="button" tabIndex={0}
              onClick={() => setDetail(r)}
              onKeyDown={(e) => { if (e.key === 'Enter') setDetail(r); }}
              style={{ border: '1px solid var(--border)', borderRadius: 12, padding: 12, background: 'var(--surface2)', marginBottom: 10, cursor: 'pointer' }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
                <span style={{ font: '600 13px Outfit, sans-serif' }}>{r.driver || 'Delivery'}</span>
                <span style={{ font: '700 13px monospace' }}>{fmtKg(r.actualWeight)} kg</span>
              </div>
              <div style={{ font: '400 12px Outfit, sans-serif', color: 'var(--muted)', marginTop: 4 }}>
                {dayjs(r.receivedOn).format('D MMM YYYY')} · {(r.files || []).length} cop{(r.files || []).length === 1 ? 'y' : 'ies'}
              </div>
            </div>
          ))}

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '10px 4px', flexWrap: 'wrap' }}>
            <span style={{ font: '400 12px Outfit, sans-serif', color: 'var(--muted)' }}>
              {total} deliver{total === 1 ? 'y' : 'ies'}
            </span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Select
                value={pageSize}
                onChange={(size) => { setPageSize(size); setPage(1); fetchReceipts({ pageSize: size, page: 1 }); }}
                options={PAGE_SIZE_OPTIONS.map((n) => ({ value: n, label: `${n} / page` }))}
                style={{ width: 110 }}
              />
              <PageBtn disabled={page <= 1} onClick={() => { setPage(page - 1); fetchReceipts({ page: page - 1 }); }}>‹</PageBtn>
              <span style={{ font: '600 12px monospace', color: 'var(--muted)' }}>{page} / {lastPage}</span>
              <PageBtn disabled={page >= lastPage} onClick={() => { setPage(page + 1); fetchReceipts({ page: page + 1 }); }}>›</PageBtn>
            </div>
          </div>
        </div>

        {/* One delivery, and its challan copies */}
        <PanelDrawer
          open={!!detail}
          onClose={() => setDetail(null)}
          darkMode={darkMode}
          width={isMobile ? '100%' : 560}
          title={detail?.driver || 'Delivery'}
          subtitle={detail?.receivedOn ? dayjs(detail.receivedOn).format('D MMMM YYYY') : undefined}
          bodyPadding={18}
        >
          {detail && (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12, paddingBottom: 14, borderBottom: '1px solid var(--border)' }}>
                <SummaryStat label="As per challan" value={detail.chalanWeight === null ? '—' : fmtKg(detail.chalanWeight)} tint="var(--muted)" />
                <SummaryStat label="Weighed" value={fmtKg(detail.actualWeight)} tint="var(--text)" />
                <SummaryStat
                  label="Variance"
                  value={detail.variance === null || detail.variance === undefined ? '—' : `${detail.variance > 0 ? '+' : ''}${fmtKg(detail.variance)}`}
                  tint={varianceTint(detail.variance)}
                />
              </div>

              <div style={{ padding: '12px 0', borderBottom: '1px solid var(--border)', font: '400 12.5px Outfit, sans-serif', color: 'var(--muted)' }}>
                {detail.totalRolls} roll{detail.totalRolls === 1 ? '' : 's'}
                {detail.user && <> · received by {detail.user}</>}
              </div>

              <div style={{ paddingTop: 12 }}>
                <div style={{ ...miniLabel, marginBottom: 8 }}>Fabric received</div>
                {(detail.lines || []).map((l) => (
                  <div key={l.id} style={{ display: 'flex', justifyContent: 'space-between', gap: 10, padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
                    <span style={{ font: '400 12.5px Outfit, sans-serif' }}>{l.fabricType} · {l.fabricColor}</span>
                    <span style={{ font: '600 12.5px monospace', color: 'var(--muted)' }}>{fmtKg(l.weight)} kg · {l.rolls} rolls</span>
                  </div>
                ))}
              </div>

              {/* The challan copies. Links are presigned and short-lived, which
                  is why they open in a new tab rather than being downloaded
                  through anything that might hold on to the URL. */}
              <div style={{ paddingTop: 16 }}>
                <div style={{ ...miniLabel, marginBottom: 8 }}>Challan copies</div>
                {(detail.files || []).length === 0 ? (
                  <div style={{ font: '400 12.5px Outfit, sans-serif', color: 'var(--dim)' }}>
                    No copy was attached to this delivery.
                  </div>
                ) : (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 10 }}>
                    {detail.files.map((f) => (
                      <a
                        key={f.id}
                        className="et-rec__file"
                        href={f.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        title={f.name}
                        style={{
                          display: 'block', border: '1px solid var(--border)', borderRadius: 10,
                          overflow: 'hidden', background: 'var(--surface2)', textDecoration: 'none', color: 'inherit',
                        }}
                      >
                        <div style={{ height: 104, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--surface)' }}>
                          {f.isPdf ? (
                            <FilePdfOutlined style={{ fontSize: 30, color: STATUS.errorSoft }} />
                          ) : (
                            <img
                              src={f.url}
                              alt={f.name}
                              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            />
                          )}
                        </div>
                        <div style={{ padding: '7px 9px' }}>
                          <div style={{ font: '600 11.5px Outfit, sans-serif', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {f.name}
                          </div>
                          <div style={{ font: '400 10.5px Outfit, sans-serif', color: 'var(--dim)' }}>
                            {f.isPdf ? 'PDF' : 'Image'} · {fmtSize(f.size)}
                          </div>
                        </div>
                      </a>
                    ))}
                  </div>
                )}
              </div>

              {detail.note && (
                <div style={{ marginTop: 14, padding: 10, borderRadius: 8, background: 'var(--surface2)', font: '400 12.5px Outfit, sans-serif', color: 'var(--muted)' }}>
                  {detail.note}
                </div>
              )}
            </>
          )}
        </PanelDrawer>
      </ConfigProvider>
    </div>
  );
}

/**
 * Weighed minus claimed. Short is the one that costs money, so it is the one
 * that gets the warning colour; over is merely worth noticing.
 */
function varianceTint(variance) {
  if (variance === null || variance === undefined) return 'var(--muted)';
  if (variance < -0.005) return STATUS.warn;
  if (variance > 0.005) return STATUS.ok;
  return 'var(--muted)';
}

function EmptyState() {
  return (
    <div style={{ padding: '32px 18px 36px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, textAlign: 'center' }}>
      <span style={{ width: 38, height: 38, borderRadius: 12, background: 'var(--surface2)', color: 'var(--dim)', fontSize: 15, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>▤</span>
      <div style={{ font: '600 13.5px Outfit, sans-serif' }}>No deliveries in this range</div>
      <div style={{ font: '400 12.5px/1.5 Outfit, sans-serif', color: 'var(--muted)', maxWidth: 320 }}>
        Fabric weighed in against a supplier challan shows up here, with the scans attached to it.
      </div>
    </div>
  );
}

export default FabricReceipts;
